import React from 'react';
import useSEO from "../hooks/useSEO";

const Privacy = () => {
    useSEO({
        title: "Zásady ochrany osobních údajů | UrbanFyzio",
        description: "Informace o zpracování osobních údajů na webu UrbanFyzio v souladu s GDPR.",
        path: "/zasady-ochrany-osobnich-udaju",
    });

    return (
        <div className="bg-surface min-h-screen">
            <div className="container-narrow section py-14 md:py-20">
                <div className="max-w-3xl mx-auto">
                    <div className="mb-10">
                        <span className="badge-brand mb-3">GDPR</span>
                        <h1 className="text-3xl md:text-5xl font-bold text-ink-900 leading-tight">
                            Zásady ochrany osobních údajů
                        </h1>
                        <span className="accent-bar mt-5" />
                        <p className="mt-5 text-ink-500 text-sm">Účinné od 1. ledna 2026</p>
                    </div>

                    <div className="card p-6 md:p-10 space-y-10 text-ink-700 leading-relaxed">

                        <Section title="1. Správce osobních údajů">
                            <p>
                                Správcem osobních údajů zpracovávaných v souvislosti s tímto webem je:
                            </p>
                            <ul className="mt-4 space-y-2 list-none">
                                <li><strong className="text-ink-900">Mgr. Šimon Urban</strong> — fyzioterapeut</li>
                                <li>Sídlo: Palackého 131, 738 01 Frýdek-Místek</li>
                                <li>E-mail: <a href="mailto:info@urbanfyzio.cz" className="text-brand-700 hover:underline">info@urbanfyzio.cz</a></li>
                                <li>Telefon: <a href="tel:+420734312843" className="text-brand-700 hover:underline">+420 734 312 843</a></li>
                            </ul>
                            <p className="mt-4 text-sm text-ink-500">
                                Zpracování probíhá v souladu s Nařízením (EU) 2016/679 (GDPR)
                                a zákonem č. 110/2019 Sb., o zpracování osobních údajů.
                            </p>
                        </Section>

                        <Section title="2. Jaké údaje zpracovávám">
                            <p>
                                Samotný web UrbanFyzio.cz neobsahuje kontaktní formuláře ani přihlašování
                                a o návštěvníkovi sám nesbírá žádné osobní údaje.
                            </p>
                            <p className="mt-4">
                                Údaje mohou být zpracovávány pouze v následujících případech:
                            </p>
                            <ul className="mt-3 list-disc list-inside space-y-2">
                                <li>Když mi napíšete na e-mail nebo zavoláte — zpracuji údaje, které mi sami sdělíte (jméno, kontakt, popis problému).</li>
                                <li>Při návštěvě webu mohou být dočasně zpracovávány technické údaje (IP adresa, typ prohlížeče) ze strany poskytovatelů třetích stran (viz bod 4).</li>
                                <li>Při rezervaci přes externí platformu Reservio jsou údaje zpracovávány touto platformou — viz <a href="https://reservio.com/" target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">zásady Reservio</a>.</li>
                            </ul>
                        </Section>

                        <Section title="3. Účel a právní základ zpracování">
                            <ul className="space-y-3">
                                <li>
                                    <strong className="text-ink-900">Komunikace a domluva termínu</strong> — právní základ: oprávněný zájem (čl. 6 odst. 1 písm. f GDPR) a plnění opatření před uzavřením smlouvy.
                                </li>
                                <li>
                                    <strong className="text-ink-900">Poskytnutí fyzioterapeutické péče</strong> — právní základ: plnění smlouvy a plnění právní povinnosti vyplývající ze zákona o zdravotních službách.
                                </li>
                                <li>
                                    <strong className="text-ink-900">Provoz webu</strong> — právní základ: oprávněný zájem na fungování a bezpečnosti webových stránek.
                                </li>
                            </ul>
                        </Section>

                        <Section title="4. Třetí strany a webové technologie">
                            <p>
                                Tento web využívá několik služeb třetích stran, které mohou samostatně zpracovávat
                                technické údaje (zpravidla IP adresu):
                            </p>
                            <ul className="mt-3 list-disc list-inside space-y-2">
                                <li><strong>Reservio</strong> — externí rezervační platforma (po kliknutí na „Rezervace"). Reservio je samostatným správcem těchto údajů.</li>
                                <li><strong>OpenStreetMap</strong> — poskytovatel mapových podkladů zobrazené mapy. Při načtení mapy jsou serverům OpenStreetMap odeslány standardní technické údaje.</li>
                                <li><strong>Google Fonts</strong> — písmo Inter je načítáno z CDN společnosti Google. Při zobrazení stránky může být odeslána IP adresa.</li>
                            </ul>
                            <p className="mt-4">
                                Tento web sám neukládá sledovací cookies a v současné době nepoužívá analytické nástroje.
                            </p>
                        </Section>

                        <Section title="5. Doba uchování údajů">
                            <ul className="list-disc list-inside space-y-2">
                                <li>E-mailová a telefonická korespondence: maximálně 3 roky od posledního kontaktu, není-li delší doba odůvodněna zdravotní péčí.</li>
                                <li>Zdravotnická dokumentace klientů: podle platných právních předpisů (zákon č. 372/2011 Sb. a vyhláška č. 98/2012 Sb.).</li>
                                <li>Údaje zpracovávané externími službami: dle podmínek příslušného poskytovatele.</li>
                            </ul>
                        </Section>

                        <Section title="6. Vaše práva">
                            <p>V souvislosti s ochranou osobních údajů máte následující práva:</p>
                            <ul className="mt-3 list-disc list-inside space-y-2">
                                <li>právo na přístup k osobním údajům</li>
                                <li>právo na opravu nepřesných údajů</li>
                                <li>právo na výmaz („právo být zapomenut")</li>
                                <li>právo na omezení zpracování</li>
                                <li>právo na přenositelnost údajů</li>
                                <li>právo vznést námitku proti zpracování</li>
                                <li>právo odvolat udělený souhlas</li>
                                <li>právo podat stížnost u <a href="https://www.uoou.cz/" target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline">Úřadu pro ochranu osobních údajů</a></li>
                            </ul>
                            <p className="mt-4">
                                Pro uplatnění jakéhokoliv z těchto práv mě prosím kontaktujte na
                                {' '}<a href="mailto:info@urbanfyzio.cz" className="text-brand-700 hover:underline">info@urbanfyzio.cz</a>.
                                Odpovím nejpozději do 30 dnů.
                            </p>
                        </Section>

                        <Section title="7. Změny zásad">
                            <p>
                                Tyto zásady mohou být průběžně aktualizovány, zejména v souvislosti se změnami
                                v poskytovaných službách nebo právních předpisech. Aktuální verze je vždy k dispozici
                                na této stránce.
                            </p>
                        </Section>

                    </div>
                </div>
            </div>
        </div>
    );
};

const Section = ({ title, children }) => (
    <section>
        <h2 className="text-xl md:text-2xl font-semibold text-ink-900 mb-4">{title}</h2>
        <div className="text-ink-700">{children}</div>
    </section>
);

export default Privacy;
